import { ACTIVE_LOCALES } from "~/i18n/i18n.consts";
import { resolveStrapiLink } from "~/utils/strapi.utils";

/** Every non-empty public locale prefix, e.g. ["/fr", "/de", …]. */
const LOCALE_PREFIXES = Object.values(ACTIVE_LOCALES)
  .map(({ urlPrefixWithSlash }) => urlPrefixWithSlash)
  .filter(Boolean);

/** True when a path already opens with one of the public locale prefixes. */
const hasLocalePrefix = (path: string): boolean =>
  LOCALE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );

/**
 * Returns a localized link by attaching urlPrefix.
 */
export const localizeLink = ({
  link,
  urlPrefix = "",
}: {
  link?: string | null;
  urlPrefix?: string;
}): string | undefined => {
  const resolved = resolveStrapiLink(link);

  if (!resolved || !urlPrefix || !resolved.startsWith("/")) {
    return resolved;
  }

  return hasLocalePrefix(resolved) ? resolved : `${urlPrefix}${resolved}`;
};

/**
 * Serializes a params object (such as Next.js `searchParams`) into a query
 * string, preserving repeated keys (array values) and skipping undefined ones.
 * Useful for carrying query params across server-side redirects so they aren't
 * silently dropped.
 * @param params - a record of params, e.g. Next.js `searchParams`
 * @returns string - the query string WITHOUT a leading "?" (empty when no params)
 */
export const serializeSearchParams = (
  params?: Record<string, string | string[] | undefined>,
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, v));
    } else if (value !== undefined) {
      search.set(key, value);
    }
  }
  return search.toString();
};
